/**
 * ShellLayout — router-connected NuShell wrapper: the NEW layout for
 * authenticated main routes (#601 S3 visual adoption).
 *
 * Supersedes SidebarMenu + TopHeader inside MainLayout for the main route
 * tree (those files stay intact — other layouts/tests still reference them).
 * NOT an access-control change: AuthGuard/RoleGuard in src/routes/index.tsx
 * are untouched; nav filtering here is VISIBILITY only (the live RouteGuard
 * swap is a separate post-#641 item).
 *
 * Wiring:
 *  - role → visibleNavForRole (canonical roles get matrix-filtered kit nav;
 *    non-normalizing legacy sessions keep the legacy SidebarMenu nav).
 *  - theme → the existing ThemeContext mechanism (data-theme + .dark
 *    lockstep, S1-1).
 *  - logout → useAuthContext().logout (tokenStore seam), then /login.
 *  - title → active nav item's label (breadcrumb/eyebrow detail stays with
 *    each page's NuScreen `eyebrow` slot).
 */

import React, { useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { PageTransition } from "../animations/PageTransition";
import { NuShell } from "./NuShell";
import { visibleNavForRole, type ShellNavItem } from "./navConfig";

/** Find the label of the nav item matching the current path (self or child). */
function titleForRoute(
  items: readonly ShellNavItem[],
  path: string,
): string | undefined {
  for (const item of items) {
    const child = item.children?.find((c) => c.route === path);
    if (child) return child.label;
    if (item.route === path) return item.label;
  }
  return undefined;
}

const ShellLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const { darkMode, toggleDarkMode } = useTheme();
  const theme = darkMode ? "dark" : "light";

  const nav = useMemo(() => visibleNavForRole(user?.role), [user?.role]);
  const allItems = useMemo(
    () => [...nav.groups.flatMap((g) => g.items), ...nav.footer],
    [nav],
  );
  const title =
    titleForRoute(allItems, location.pathname) ??
    (location.pathname === "/dashboard" ? "Dashboard" : undefined);

  return (
    <NuShell
      route={location.pathname}
      onNavigate={(route) => navigate(route)}
      theme={theme}
      onToggleTheme={toggleDarkMode}
      navGroups={nav.groups}
      navFooter={nav.footer}
      title={title}
      user={
        user
          ? { name: user.username, email: user.email, role: user.role }
          : undefined
      }
      onLogout={logout}
    >
      <PageTransition transitionKey={location.pathname} animation="fadeIn">
        <Outlet />
      </PageTransition>
    </NuShell>
  );
};

export default ShellLayout;
